import React, { createContext, useContext, useState } from 'react';
  
import api from '../services/api';
// Create the context
const DashboardContext = createContext();

export const useDashboard = () => useContext(DashboardContext);

const MAX_RETRIES = 3;
const RETRY_DELAY = 1000; // 1 second

const retryApiCall = async (apiCall, retryCount = 0) => {
  try {
    return await apiCall();
  } catch (error) {
    const isCorsError = error.message?.includes('CORS') || 
                       error.message?.includes('Network Error') ||
                       error.message?.includes('Failed to fetch');

    if (isCorsError && retryCount < MAX_RETRIES) {
      await new Promise(resolve => setTimeout(resolve, RETRY_DELAY));
      return retryApiCall(apiCall, retryCount + 1);
    }
    throw error;
  }
};

export const DashboardProvider = ({ children }) => {
  
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);


  const fetchGeneralStats = async (stats_type, startDate, endDate) => {
    try {
      const response = await retryApiCall(() => 
        api.get(`/dashboard/general-stats/${stats_type}?start_date=${startDate}&end_date=${endDate}`)
      );
      return response.data;
    } catch (error) {
      setError(error);
      return null;
    } finally {
      setLoading(false);
    }
  };

  const fetchSalesLeadFunnel = async (startDate, endDate) => {
    try {
      const response = await api.get(`/dashboard/sales-lead-funnel/?start_date=${startDate}&end_date=${endDate}`);
      return response.data;
    } catch (error) {
      setError(error);
    } finally {
      setLoading(false);
    }
  };


  const fetchSalesLeadFunnelMatrics = async (startDate, endDate) => {
    try {
      const response = await api.get(`/dashboard/sales-lead-funnel-matrics/?start_date=${startDate}&end_date=${endDate}`);
      return response.data;
    } catch (error) {
      setError(error);
    } finally {
      setLoading(false);
    }
  };

  const fetchLeadConversionRatio = async (startDate, endDate) => {
    try {
      const response = await api.get(`/dashboard/lead-conversion-ratio/?start_date=${startDate}&end_date=${endDate}`);
      return response.data;
    } catch (error) {
      setError(error);
    } finally {
      setLoading(false);
    }
  };

  const fetchLeadSourceMetrics = async (startDate, endDate) => {    
    try {
      const response = await api.get('/dashboard/lead-source-metrics/?start_date=' + startDate + '&end_date=' + endDate);
      return response.data;
    } catch (error) {
      setError(error);
      return null;
    } finally {
      setLoading(false);    
    }
  };

  const fetchTodaysReport = async () => {
    try {
      const response = await api.get('/dashboard/todays-report/');
      return response.data;
    } catch (error) {
      setError(error);
      return null;
    } finally {
      setLoading(false);    
    }
  };

  const fetchBacklogTasks = async () => {
    try {
      const response = await api.get('/dashboard/backlog-tasks/');
      return response.data;
    } catch (error) { 
      setError(error);
      return null;
    } finally {
      setLoading(false);    
    }
  };

  const fetchSiteVisits = async () => {
    try {
      const response = await api.get('/dashboard/site-visits/');
      return response.data;
    } catch (error) { 
      setError(error);
      return null;
    } finally {
      setLoading(false);    
    }
  };



  const fetchAgentPerformance = async (type, startDate, endDate) => {
    console.log('fetchAgentPerformance', type, startDate, endDate);
    try {
      const response = await api.get('/dashboard/agent-performance/?type=' + type + '&start_date=' + startDate + '&end_date=' + endDate);
      return response.data;
    } catch (error) {
      setError(error);
    } finally {
      setLoading(false);    
    }
  };

  const fetchRecentFlaggedLeads = async () => {
    try {
      const response = await api.get('/dashboard/recent-flagged-leads/');
      return response.data;
    } catch (error) {
      setError(error);
    } finally {
      setLoading(false);    
    }
  };

  const fetchRecentLostDeals = async () => {
    try {
      const response = await api.get('/dashboard/recent-lost-deals/');
      return response.data;
    } catch (error) {
      setError(error);
    } finally {
      setLoading(false);    
    }
  };

  // Context value
  const value = {
    // Core operations
    fetchGeneralStats,
    fetchSalesLeadFunnel,
    fetchSalesLeadFunnelMatrics,
    fetchLeadConversionRatio,
    fetchLeadSourceMetrics,
    fetchTodaysReport,
    fetchBacklogTasks,
    fetchSiteVisits,
    fetchAgentPerformance,
    fetchRecentFlaggedLeads,
    fetchRecentLostDeals,
  };

  return (
    <DashboardContext.Provider value={value}>
      {children}
    </DashboardContext.Provider>
  );
};

export default DashboardProvider; 